import { useEffect, useRef, useState } from "react";
import { apiServerUrl } from "../services/ApiService";
import { getStatusLabel } from "../utils";

const SESSION_KEY = "adminSecret";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

interface Administration {
  code: number;
  name: string;
}

interface TenantGroup {
  tenant_id: number | null;
  administrations: Administration[];
}

interface OperatingCompany {
  name: string;
  code: string;
}

interface AdministrationStats {
  code: number;
  name: string;
  tenant_id: number;
  runs: number;
  proefdraaien: number;
  results: Record<string, number>;
}

interface OverviewTotals {
  tenant_count: number;
  administration_count: number;
  runs: number;
  proefdraaien: number;
  results: Record<string, number>;
}

interface OverviewData {
  administrations: AdministrationStats[];
  totals: OverviewTotals;
}

// --------------------------------------------------------------------------
// Secret login screen
// --------------------------------------------------------------------------

const SecretLogin = ({ onSuccess }: { onSuccess: (secret: string) => void }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${apiServerUrl}/admin/verify`, {
        method: "POST",
        headers: { "X-Admin-Secret": value.trim() },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Request failed (${res.status})`);
      }
      sessionStorage.setItem(SESSION_KEY, value.trim());
      onSuccess(value.trim());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-gray-900 mb-6">Admin Portal</h1>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="password"
            placeholder="Admin secret"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !value.trim()}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors
              bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {submitting ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Overview tab
// --------------------------------------------------------------------------

const RESULT_COLUMNS: { key: string; label: string }[] = [
  { key: "SUCCESS", label: getStatusLabel("SUCCESS") },
  { key: "PARTIAL_SUCCESS", label: getStatusLabel("PARTIAL_SUCCESS") },
  { key: "FAILED", label: getStatusLabel("FAILED") },
  { key: "CANCELLED", label: getStatusLabel("CANCELLED") },
  { key: "PENDING", label: "Nog niet afgerond" },
];

const Overview = ({ secret }: { secret: string }) => {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${apiServerUrl}/admin/overview`, { headers: { "X-Admin-Secret": secret } })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((d: OverviewData) => setData(d))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load overview"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (error) return <p className="text-sm text-red-500">{error}</p>;
  if (!data) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="py-2 pr-3 text-left font-medium text-gray-500">Tenants</th>
            <th className="py-2 px-3 text-left font-medium text-gray-500 min-w-[220px] whitespace-nowrap">
              Administraties
            </th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Runs</th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Proefdraaien</th>
            {RESULT_COLUMNS.map((col) => (
              <th key={col.key} className="py-2 px-3 text-right font-medium text-gray-500">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          <tr className="font-medium border-b-2 border-gray-200">
            <td className="py-2 pr-3 text-gray-800 tabular-nums">{data.totals.tenant_count}</td>
            <td className="py-2 px-3 text-gray-800 tabular-nums whitespace-nowrap">{data.totals.administration_count}</td>
            <td className="py-2 px-3 text-right tabular-nums text-gray-900">{data.totals.runs}</td>
            <td className="py-2 px-3 text-right tabular-nums text-gray-900">{data.totals.proefdraaien}</td>
            {RESULT_COLUMNS.map((col) => (
              <td key={col.key} className="py-2 px-3 text-right tabular-nums text-gray-900">
                {data.totals.results[col.key] ?? 0}
              </td>
            ))}
          </tr>
          {data.administrations.map((a) => (
            <tr key={a.code}>
              <td className="py-2 pr-3 text-gray-800 tabular-nums">{a.tenant_id}</td>
              <td className="py-2 px-3 text-gray-800 whitespace-nowrap">
                {a.name} <span className="text-gray-400">({a.code})</span>
              </td>
              <td className="py-2 px-3 text-right tabular-nums text-gray-900">{a.runs}</td>
              <td className="py-2 px-3 text-right tabular-nums text-gray-900">{a.proefdraaien}</td>
              {RESULT_COLUMNS.map((col) => (
                <td key={col.key} className="py-2 px-3 text-right tabular-nums text-gray-900">
                  {a.results[col.key] ?? 0}
                </td>
              ))}
            </tr>
          ))}
          {data.administrations.length === 0 && (
            <tr>
              <td colSpan={4 + RESULT_COLUMNS.length} className="py-4 text-center text-gray-400">
                Geen administraties gevonden
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// --------------------------------------------------------------------------
// Auto Login tab
// --------------------------------------------------------------------------

const AutoLogin = ({ secret }: { secret: string }) => {
  const [tenants, setTenants] = useState<TenantGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<number>>(new Set());
  const [userEfRegistrationId, setUserEfRegistrationId] = useState("0");
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const tokenRef = useRef<HTMLTextAreaElement>(null);
  const [editingTenant, setEditingTenant] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const headers = { "X-Admin-Secret": secret };

  const loadTenants = () => {
    fetch(`${apiServerUrl}/admin/tenants`, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((data: { tenants: TenantGroup[] }) => setTenants(data.tenants))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load tenants"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTenantSelect = (tenantId: number | null) => {
    setSelectedTenantId(tenantId);
    setEditingTenant(false);
    const group = tenants.find((t) => t.tenant_id === tenantId);
    setSelectedCodes(new Set(group?.administrations.map((a) => a.code) ?? []));
  };

  const handleAddAdministration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTenantId === null || !newCode.trim() || !newName.trim()) return;
    setEditError(null);
    setEditSubmitting(true);
    try {
      const res = await fetch(`${apiServerUrl}/admin/administrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ tenant_id: selectedTenantId, code: Number(newCode), name: newName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Request failed (${res.status})`);
      }
      const added = (await res.json()) as { code: number };
      setNewCode("");
      setNewName("");
      loadTenants();
      setSelectedCodes((prev) => new Set(prev).add(added.code));
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : "Failed to add administration");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleRemoveAdministration = async (removeCode: number) => {
    setEditError(null);
    try {
      const res = await fetch(`${apiServerUrl}/admin/administrations/${removeCode}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Request failed (${res.status})`);
      }
      loadTenants();
      setSelectedCodes((prev) => {
        const next = new Set(prev);
        next.delete(removeCode);
        return next;
      });
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : "Failed to remove administration");
    }
  };

  const toggleCode = (code: number) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const generateToken = async (): Promise<string> => {
    const body = new FormData();
    body.append("tenant_id", String(selectedTenantId));
    selectedCodes.forEach((code) => body.append("codes", String(code)));
    body.append("user_ef_registration_id", userEfRegistrationId.trim() || "0");

    const res = await fetch(`${apiServerUrl}/admin/login`, { method: "POST", headers, body });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as { detail?: string }).detail ?? `Login failed (${res.status})`);
    }
    const { token: t } = (await res.json()) as { token: string };
    return t;
  };

  const handleLogin = async () => {
    if (selectedTenantId === null || selectedCodes.size === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const t = await generateToken();
      window.location.href = `/?token=${encodeURIComponent(t)}`;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Login failed");
      setSubmitting(false);
    }
  };

  const handleGenerateOnly = async () => {
    if (selectedTenantId === null || selectedCodes.size === 0) return;
    setError(null);
    setToken(null);
    setSubmitting(true);
    try {
      const t = await generateToken();
      setToken(t);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to generate token");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (error) return <p className="text-sm text-red-500">{error}</p>;

  const canSubmit = selectedTenantId !== null && selectedCodes.size > 0 && !submitting;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs text-gray-500 mb-1">user_ef_registration_id</label>
        <input
          type="number"
          value={userEfRegistrationId}
          onChange={(e) => setUserEfRegistrationId(e.target.value)}
          placeholder="0"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div className="space-y-2">
        {tenants.map((group) => {
          const isSelected = selectedTenantId === group.tenant_id;
          return (
            <div
              key={group.tenant_id ?? "none"}
              className={`rounded-lg border transition-colors overflow-hidden ${
                isSelected ? "border-blue-400" : "border-gray-200"
              }`}
            >
              <div
                className={`flex items-center gap-3 px-4 py-3 ${
                  isSelected ? "bg-blue-50" : "bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <label className="flex items-center gap-3 flex-1 cursor-pointer">
                  <input
                    type="radio"
                    name="tenant_id"
                    checked={isSelected}
                    onChange={() => handleTenantSelect(group.tenant_id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Tenant {group.tenant_id ?? "—"}
                  </span>
                </label>
                <span className="text-xs text-gray-400">
                  {group.administrations.length} admin{group.administrations.length !== 1 ? "s" : ""}
                </span>
                {isSelected && (
                  <button
                    type="button"
                    onClick={() => setEditingTenant((v) => !v)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {editingTenant ? "Done" : "Edit"}
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-100">
                {group.administrations.map((admin) => (
                  <div
                    key={admin.code}
                    className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                      isSelected ? "hover:bg-blue-50/50" : "opacity-40"
                    }`}
                  >
                    <label className={`flex items-center gap-3 flex-1 ${isSelected ? "cursor-pointer" : "cursor-default"}`}>
                      <input
                        type="checkbox"
                        disabled={!isSelected}
                        checked={isSelected && selectedCodes.has(admin.code)}
                        onChange={() => isSelected && toggleCode(admin.code)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-default"
                      />
                      <span className="text-sm text-gray-700">{admin.name}</span>
                      <span className="ml-auto text-xs text-gray-400 tabular-nums">{admin.code}</span>
                    </label>
                    {isSelected && editingTenant && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAdministration(admin.code)}
                        className="text-gray-300 hover:text-red-400 text-lg leading-none px-1"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {isSelected && editingTenant && group.tenant_id !== null && (
                <form onSubmit={handleAddAdministration} className="px-4 py-3 border-t border-gray-100 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="naam"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="code"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                      className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {editError && <p className="text-sm text-red-500">{editError}</p>}
                  <button
                    type="submit"
                    disabled={editSubmitting || !newCode.trim() || !newName.trim()}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors
                      bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {editSubmitting ? "Adding…" : "Add Administration"}
                  </button>
                </form>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleLogin}
          disabled={!canSubmit}
          className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors
            bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {submitting ? "Logging in…" : "Login"}
        </button>
        <button
          onClick={handleGenerateOnly}
          disabled={!canSubmit}
          className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors
            bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
        >
          {submitting ? "Generating…" : "Generate Token"}
        </button>
      </div>

      {token && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Access token</span>
            <button onClick={handleCopy} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <textarea
            ref={tokenRef}
            readOnly
            value={token}
            rows={4}
            onClick={() => tokenRef.current?.select()}
            className="w-full text-xs font-mono border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 break-all"
          />
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------
// Create Access Token tab
// --------------------------------------------------------------------------

const MODULES = ["sync_hub", "data_studio"];

const CreateAccessToken = ({ secret }: { secret: string }) => {
  const [userEmail, setUserEmail] = useState("test@zypp.io");
  const [registrationId, setRegistrationId] = useState("0");
  const [tenantId, setTenantId] = useState("0");
  const [module, setModule] = useState("sync_hub");
  const [companies, setCompanies] = useState<OperatingCompany[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const tokenRef = useRef<HTMLTextAreaElement>(null);

  const updateCompany = (index: number, field: keyof OperatingCompany, value: string) => {
    setCompanies((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const addCompany = () => setCompanies((prev) => [...prev, { name: "", code: "" }]);
  const removeCompany = (index: number) => setCompanies((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    setError(null);
    setToken(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${apiServerUrl}/admin/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Secret": secret },
        body: JSON.stringify({
          user_email: userEmail,
          user_ef_registration_id: Number(registrationId),
          tenant_id: Number(tenantId),
          module,
          operating_companies: companies
            .filter((c) => c.name.trim() && c.code.trim())
            .map((c) => ({ name: c.name.trim(), code: Number(c.code) })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Request failed (${res.status})`);
      }
      const { token: t } = (await res.json()) as { token: string };
      setToken(t);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create token");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">user_email</label>
          <input
            type="text"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">user_ef_registration_id</label>
          <input
            type="number"
            value={registrationId}
            onChange={(e) => setRegistrationId(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">tenant_id</label>
          <input
            type="number"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">module</label>
          <select
            value={module}
            onChange={(e) => setModule(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500">operating_companies</label>
          <button type="button" onClick={addCompany} className="text-xs text-blue-600 hover:text-blue-800">
            + Add
          </button>
        </div>
        <div className="space-y-2">
          {companies.map((company, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Company name"
                value={company.name}
                onChange={(e) => updateCompany(i, "name", e.target.value)}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Code"
                value={company.code}
                onChange={(e) => updateCompany(i, "code", e.target.value)}
                className="w-32 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => removeCompany(i)}
                className="text-gray-300 hover:text-red-400 text-lg leading-none px-1"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors
          bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
      >
        {submitting ? "Generating…" : "Generate Token"}
      </button>

      {token && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Access token</span>
            <button onClick={handleCopy} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <textarea
            ref={tokenRef}
            readOnly
            value={token}
            rows={4}
            onClick={() => tokenRef.current?.select()}
            className="w-full text-xs font-mono border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 break-all"
          />
          <button
            onClick={() => { window.location.href = `/?token=${encodeURIComponent(token)}`; }}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors bg-green-600 text-white hover:bg-green-700"
          >
            Open app with this token
          </button>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------
// Admin portal container with sidebar navigation
// --------------------------------------------------------------------------

type Section = "authenticate" | "dashboard";
type AuthTab = "auto-login" | "create-token";

const SECTIONS: { id: Section; label: string }[] = [
  { id: "authenticate", label: "Authenticate" },
  { id: "dashboard", label: "Dashboard" },
];

const AdminPortal = () => {
  const [secret, setSecret] = useState<string>(() => sessionStorage.getItem(SESSION_KEY) ?? "");
  const [section, setSection] = useState<Section>("authenticate");
  const [authTab, setAuthTab] = useState<AuthTab>("auto-login");

  if (!secret) {
    return <SecretLogin onSuccess={setSecret} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-7xl flex gap-10">
        <aside className="w-44 shrink-0">
          <h1 className="text-xl font-semibold text-gray-900 mb-6">Admin Portal</h1>
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  section === id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <button
            onClick={() => { sessionStorage.removeItem(SESSION_KEY); setSecret(""); }}
            className="mt-6 px-3 text-xs text-gray-400 hover:text-gray-600"
          >
            Sign out
          </button>
        </aside>

        <main className="flex-1 min-w-0">
          {section === "dashboard" && <Overview secret={secret} />}
          {section === "authenticate" && (
            <div>
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-6 max-w-sm">
                {([
                  { id: "auto-login", label: "Auto Login" },
                  { id: "create-token", label: "Create Access Token" },
                ] as { id: AuthTab; label: string }[]).map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setAuthTab(id)}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      authTab === id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="max-w-md">
                {authTab === "auto-login" && <AutoLogin secret={secret} />}
                {authTab === "create-token" && <CreateAccessToken secret={secret} />}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPortal;
